import React, { useState, useRef, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { c as classnames } from './index-2586sEvh.js';
import { CoreContext } from './context/index.js';
import './utils/logToSlack.js';
import './animations--PMkOS0j.js';
import 'cookies-next';
import './currency-_kacFJHO.js';
import './language-BHHwgtQp.js';
import './payment-YC9H491Y.js';
import './index-tjIjLI8L.js';
import useShowError from './hooks/useShowError.js';
import 'notistack';
import { getProductLink, isProductAvailableForSale } from './utils/product.js';
import './helpers-kA_hdoOI.js';
import 'lodash';
import { d as dataLayerSelectItemAccor } from './selectItem-PWuzvZX7.js';
import useItemListName from './hooks/useItemListName.js';
import { getCartSessionId } from './utils/cart.js';
import { getPrices } from './utils/price.js';
import { getColorSwitcherData } from './utils/category.js';
import { getProductDiscount } from './utils/saleFlag.js';
import { logImgLoadErrorToRelic } from './utils/logToRelic.js';
import { C as ColorSwitcher } from './ColorSwitcher-UbWq37lI.js';
import { S as SaleFlag } from './SaleFlag-uqbhEU3Y.js';
import Link from './components/Link/index.js';
import { S as ScreenDetector } from './ScreenDetector-FnfvTUuh.js';
import WishlistButton from './components/WishlistButton/index.js';
import JustAddedProducts from './components/JustAddedProducts/index.js';
import { S as ShopButton } from './ShopButton-3jX2bKPw.js';
import { getImageUrl } from './utils/image.js';
import 'lodash/cloneDeep';
import { d as dataLayerSelectItem } from './selectItem-UJ5YRly2.js';
import { P as Prices } from './Prices-J1SqTpik.js';
import { P as ProductColorAttributeCode } from './product-_pRduMz7.js';

const ProductItem = ({ product, index, showPrice, showDescription, showWishlist, colorSwitcherMode = 'click', enableColorSwitcher = false, forceColorSwitcherSpacing = false, displayPrices = false, allowAboveFoldAnimation = false, lazyLoad, itemsCount, locale, }) => {
    var _a, _b, _c, _d, _e;
    const { t: tCommon } = useTranslation('', { keyPrefix: '$common' });
    const { t: tLabel } = useTranslation('', { keyPrefix: '$common.$labels' });
    const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
    const { push, asPath } = useRouter();
    const itemListName = useItemListName();
    const [justAddedData, setJustAddedData] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const productImgRef = useRef(null);
    const openModalRef = useRef(null);
    const [isProductImageLoaded, setIsProductImageLoaded] = useState(false);
    const [isScreenDetected, setIsScreenDetected] = useState(false);
    const [colorsList, setColorsList] = useState([]);
    const [colorSwitcherImagesList, setColorSwitcherImagesList] = useState([]);
    const [displayImageIndex, setDisplayImageIndex] = useState(0);
    const productImage = {
        imgUrl: ((_a = product.small_image) === null || _a === void 0 ? void 0 : _a.url) || null,
        imgUrlThumbnail: ((_b = product.thumbnail) === null || _b === void 0 ? void 0 : _b.url) || null,
        imgUrlHover: ((_c = product.image_hover) === null || _c === void 0 ? void 0 : _c.url) || null,
    };
    const imagesList = [productImage, ...colorSwitcherImagesList];
    const { data, useDispatch, thunks: { cart: { getCart, addProduct }, }, } = useContext(CoreContext);
    const { product: { imageHeight, imageWidth, imageWidthThumbnail }, category: { alternateCtaPosition = null }, siteName, parentHotelCompany, productNameFilters, accorTagging: { accorTaggingEnabled = false } = {}, promo: { displaySaleFlag = false, discount: promoDiscount = '', productsSaleExcludeList = [], } = {}, noZeroAfterDecimal = false, } = data.siteConfig;
    const dispatch = useDispatch();
    const showError = useShowError();
    const position = 'left';
    const link = getProductLink(product.url_key, product.categories, locale);
    const isProductAvailable = isProductAvailableForSale(product.backorder_status, product.stock_status);
    const oosMessage = isProductAvailable
        ? `${tLabel('outStock')}. ${tCommon('contactOurService')}`
        : '';
    const productPromoDiscount = getProductDiscount(promoDiscount, product, productsSaleExcludeList);
    const handleAddSimpleProduct = async () => {
        setIsAdding(true);
        const simpleProductPayload = {
            data: {
                sku: product.sku,
                quantity: 1,
            },
        };
        const resultAction = await dispatch(addProduct({ simpleProducts: [simpleProductPayload], accorTaggingEnabled, itemListName }));
        if (addProduct.fulfilled.match(resultAction)) {
            setJustAddedData(resultAction.payload);
            // Run getCart() to update MiniShoppingCart popup data
            dispatch(getCart({ cart_id: getCartSessionId(), accorTaggingEnabled }));
        }
        else {
            showError(resultAction.payload.errorMessage);
        }
        setIsAdding(false);
    };
    const onColorSwitcherEnter = (color) => {
        const imageIndex = colorsList.indexOf(color);
        // Using "index + 1" because the 0 element is the product main image
        // The color switcher images are starting from index 1
        setDisplayImageIndex(imageIndex + 1);
    };
    const onColorSwitcherLeave = () => {
        // Set the default product image (index = 0)
        setDisplayImageIndex(0);
    };
    const closeHandler = () => {
        setJustAddedData(null);
        if (openModalRef.current) {
            openModalRef.current.focus();
        }
    };
    const handleProductClick = (event) => {
        event.preventDefault();
        if (accorTaggingEnabled) {
            dataLayerSelectItemAccor({
                product,
                affiliation: parentHotelCompany,
                brand: siteName,
                siteFilters: productNameFilters,
                index,
                itemListName,
            });
        }
        else {
            dataLayerSelectItem({
                product,
                affiliation: parentHotelCompany,
                brand: siteName,
                locale: locale,
                siteFilters: productNameFilters,
                index,
            });
        }
        if (event.ctrlKey || event.metaKey) {
            // Open in a new tab if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
            window.open(`${link}?selectItemListName=${itemListName}`, '_blank');
            return;
        }
        push(`${link}?selectItemListName=${itemListName}`, link);
    };
    const prices = getPrices(product.price_range, locale, noZeroAfterDecimal);
    const colorAttribute = (_d = product.configurable_options) === null || _d === void 0 ? void 0 : _d.find(option => Object.values(ProductColorAttributeCode).includes(option.attribute_code));
    const handleMouseEnter = () => {
        setIsHovered(true);
    };
    const handleMouseLeave = () => {
        setIsHovered(false);
    };
    const blocNameCta = tButton('shopNow');
    const blocNameImage = `product image ${product.name}`;
    const blocNameName = `product name ${product.name}`;
    const processColorSwitcherData = () => {
        const { colors, images } = getColorSwitcherData(product);
        setColorsList(colors);
        setColorSwitcherImagesList(images);
    };
    useEffect(() => {
        if (isProductImageLoaded && isScreenDetected) {
            processColorSwitcherData();
        }
    }, [isProductImageLoaded, isScreenDetected]);
    useEffect(() => {
        var _a;
        // NextJs has an issue with img onLoad method
        // Sometimes it's not working
        // This is a fix to handle when the img was loaded
        // https://stackoverflow.com/a/59809184
        if ((_a = productImgRef === null || productImgRef === void 0 ? void 0 : productImgRef.current) === null || _a === void 0 ? void 0 : _a.complete) {
            setIsProductImageLoaded(true);
        }
    }, []);
    const hoverContent = () => (React.createElement("div", { className: "hover-content", "data-event-name": "bloc_interact", "data-bloc-name": blocNameCta },
        React.createElement(ShopButton, { buttonRef: openModalRef, product: product, link: link, isAdding: isAdding, onAddSimpleProduct: handleAddSimpleProduct, onClick: handleProductClick })));
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classnames('item product-item', position, `q${itemsCount}`, (colorAttribute || forceColorSwitcherSpacing) &&
                enableColorSwitcher &&
                'color-switcher-active'), id: product.sku, key: index },
            React.createElement("div", { className: "product-item-container" },
                React.createElement("div", { className: "stack" },
                    React.createElement(Link, { href: link, className: "item-ctg-copy", black: true, noUnderline: true, onClick: handleProductClick, noClass: true },
                        React.createElement("h2", { className: "subtitle-category font-sub-heading", "data-event-name": "bloc_interact", "data-bloc-name": blocNameName },
                            React.createElement("span", { "data-aos": "fade", "data-gsap-delay": "200", ...(!allowAboveFoldAnimation && { 'data-gsap-above-fold-stop': true }) }, ((_e = product.product_subtitle) === null || _e === void 0 ? void 0 : _e.trim())
                                ? `${product.name} - ${product.product_subtitle}`
                                : product.name)),
                        alternateCtaPosition === 'heading' && hoverContent(),
                        React.createElement(Prices, { show: displayPrices, prices: prices }),
                        enableColorSwitcher && (React.createElement(React.Fragment, null,
                            React.createElement(ScreenDetector, { onScreenDetected: () => setIsScreenDetected(true) }),
                            React.createElement(ColorSwitcher, { product: product, position: position, onClick: onColorSwitcherEnter, onMouseEnter: onColorSwitcherEnter, onMouseLeave: onColorSwitcherLeave, errors: null, colorSwitherMode: colorSwitcherMode, forceColorSwitcherSpacing: forceColorSwitcherSpacing }))),
                        showPrice && (React.createElement("div", { className: "item-price-block" },
                            prices.oldPrice && prices.oldPrice !== prices.newPrice && (React.createElement("div", { className: "item-prev-price strike" }, prices.oldPrice)),
                            prices.newPrice && React.createElement("div", { className: "item-new-price" }, prices.newPrice))),
                        showPrice && oosMessage && React.createElement("div", { className: "oos-info" }, oosMessage),
                        showDescription && (React.createElement("div", { className: "about-content-text", dangerouslySetInnerHTML: {
                                __html: product.description.html,
                            } }))),
                    React.createElement("div", { className: "ctg-image-wrapper", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
                        React.createElement(Link, { id: `CD_${product.sku}`, href: link, onClick: handleProductClick, noTabbing: true, noClass: true },
                            React.createElement(SaleFlag, { show: displaySaleFlag, discount: productPromoDiscount }),
                            React.createElement("div", { className: "ctg-image", "data-gsap": "fade", ...(!allowAboveFoldAnimation && { 'data-gsap-above-fold-stop': true }), "data-event-name": "bloc_interact", "data-bloc-name": blocNameImage }, imagesList.map((image, imageIndex) => {
                                const imgUrl = getImageUrl(image.imgUrl);
                                const imgUrlThumbnail = getImageUrl(image.imgUrlThumbnail);
                                const imgUrlHover = image.imgUrlHover ? getImageUrl(image.imgUrlHover) : null;
                                // Use lazy load only for the main (first) product image
                                // The rest color switcher images should be loaded by the screen detector flow
                                const useLazyLoad = lazyLoad && imageIndex === 0;
                                return (
                                // eslint-disable-next-line react/no-array-index-key
                                React.createElement(React.Fragment, { key: imageIndex },
                                    React.createElement("picture", { className: classnames({ hide: imageIndex !== displayImageIndex }) },
                                        React.createElement("source", { srcSet: imgUrlThumbnail.includes('placeholder') ? imgUrl : imgUrlThumbnail, media: `(max-width: ${imageWidthThumbnail}px)` }),
                                        React.createElement("img", { ...(imageIndex === 0 && { ref: productImgRef }), src: imgUrl, alt: product.name, width: imageWidth, height: imageHeight, ...(useLazyLoad && { loading: 'lazy' }), onError: () => logImgLoadErrorToRelic(asPath, imgUrl), onLoad: () => {
                                                // Change the product image (index = 0) status
                                                if (imageIndex !== 0)
                                                    return;
                                                setIsProductImageLoaded(true);
                                            } })),
                                    imgUrlHover && (React.createElement("img", { src: image.imgUrlHover, alt: product.name, width: imageWidth, height: imageHeight, className: `hover ${isHovered ? 'hover-active' : ''}`, ...(useLazyLoad && { loading: 'lazy' }), onError: () => logImgLoadErrorToRelic(asPath, imgUrlHover), loading: "lazy" }))));
                            }))),
                        alternateCtaPosition !== 'heading' && hoverContent()))),
            React.createElement("div", { className: "category-item-about" }, showWishlist && React.createElement(WishlistButton, { classNameWrapper: "item-heart-icon", product: product }))),
        justAddedData && React.createElement(JustAddedProducts, { data: justAddedData, onClose: closeHandler })));
};

export { ProductItem as P };
//# sourceMappingURL=ProductItem-f-1ZlguI.js.map
